import {
  ELEC_CAP_COM_2015,
  ELEC_CAP_IND_2015,
  ELEC_CAP_NONRES_2015,
  ELEC_CAP_NONRES_2019,
  ELEC_EMISSION_FACTOR_MT_PER_KWH,
} from './bhSourceControls';
import type { CanonicalEnergyActivityYear } from './types';
export type ChartDisplayMode = 'activity' | 'emissions';

export const ELEC_NONRES_CLASSIFICATION_BREAK_CALLOUT =
  'Commercial and industrial classifications are not necessarily comparable between the 2015 CAP and the 2021–2023 SCE release. The timing and nature of any account reclassification are unknown. Use combined nonresidential electricity when evaluating change over time.';

function pctShare(part: number, whole: number, digits = 2): string {
  if (whole <= 0) return '—';
  return ((part / whole) * 100).toFixed(digits);
}

/** Commercial share of nonresidential kWh when a verified sector split exists (2015 or SCE 2021–2023). */
export function elecNonresCommercialSharePercent(row: CanonicalEnergyActivityYear): number | null {
  if (!yearHasVerifiedElecNonresSectorSplit(row)) return null;
  const comKwh = row.year === 2015 ? ELEC_CAP_COM_2015 : row.commercial.value!;
  const indKwh = row.year === 2015 ? ELEC_CAP_IND_2015 : row.industrial.value!;
  const whole = comKwh + indKwh;
  return (comKwh / whole) * 100;
}

export interface ElecNonresChartSegments {
  commercial: number;
  industrial: number;
  otherNonresidential: number;
  undividedNonresidential: number;
  /** Chart.js placeholder sum equals canonical nonresidential total (activity or MT). */
  canonicalTotal: number;
}

function roundDisplay(n: number, mode: ChartDisplayMode): number {
  if (mode === 'emissions') return Math.round(n * 10) / 10;
  return Math.round(n);
}

/** Years with verified commercial + industrial kWh components (CAP or SCE). */
export function yearHasVerifiedElecNonresSectorSplit(row: CanonicalEnergyActivityYear): boolean {
  if (row.fuel !== 'electricity') return false;
  if (row.year === 2015) return true;
  if (row.year >= 2021 && row.year <= 2023) {
    return row.commercial.value != null && row.industrial.value != null;
  }
  return false;
}

export function buildElecNonresidentialChartSegments(
  row: CanonicalEnergyActivityYear,
  mode: ChartDisplayMode
): ElecNonresChartSegments {
  const nonresKwh = row.nonresidential.value ?? 0;
  const factor = ELEC_EMISSION_FACTOR_MT_PER_KWH;
  const canonicalTotal =
    mode === 'emissions' ? roundDisplay(nonresKwh * factor, mode) : roundDisplay(nonresKwh, mode);

  if (yearHasVerifiedElecNonresSectorSplit(row)) {
    const comKwh = row.year === 2015 ? ELEC_CAP_COM_2015 : row.commercial.value!;
    const indKwh = row.year === 2015 ? ELEC_CAP_IND_2015 : row.industrial.value!;
    const com =
      mode === 'emissions' ? roundDisplay(comKwh * factor, mode) : roundDisplay(comKwh, mode);
    const ind =
      mode === 'emissions' ? roundDisplay(indKwh * factor, mode) : roundDisplay(indKwh, mode);
    return {
      commercial: com,
      industrial: ind,
      otherNonresidential: 0,
      undividedNonresidential: 0,
      canonicalTotal,
    };
  }

  return {
    commercial: 0,
    industrial: 0,
    otherNonresidential: 0,
    undividedNonresidential: canonicalTotal,
    canonicalTotal,
  };
}

export function elecNonresYearTooltipLines(
  row: CanonicalEnergyActivityYear,
  mode: ChartDisplayMode
): string[] {
  const unit = mode === 'emissions' ? 'MT CO2e' : 'kWh';
  const fmt = (n: number) =>
    mode === 'emissions' ? n.toLocaleString(undefined, { maximumFractionDigits: 1 }) : Math.round(n).toLocaleString();

  if (row.year === 2015) {
    const comKwh = ELEC_CAP_COM_2015;
    const indKwh = ELEC_CAP_IND_2015;
    const total = mode === 'emissions'
      ? ELEC_CAP_NONRES_2015 * ELEC_EMISSION_FACTOR_MT_PER_KWH
      : ELEC_CAP_NONRES_2015;
    const com = mode === 'emissions' ? comKwh * ELEC_EMISSION_FACTOR_MT_PER_KWH : comKwh;
    const ind = mode === 'emissions' ? indKwh * ELEC_EMISSION_FACTOR_MT_PER_KWH : indKwh;
    return [
      'Source: Beverly Hills CAP',
      `Commercial: ${fmt(com)} ${unit}`,
      `Industrial: ${fmt(ind)} ${unit}`,
      `Commercial share: ${pctShare(comKwh, comKwh + indKwh)}%`,
      `Industrial share: ${pctShare(indKwh, comKwh + indKwh)}%`,
      'Classification basis: 2015 CAP',
      mode === 'emissions'
        ? `Experimental factor: ${ELEC_EMISSION_FACTOR_MT_PER_KWH} MT CO2e per kWh (common factor on components)`
        : '',
    ].filter(Boolean);
  }

  if (row.year === 2019) {
    const total = mode === 'emissions'
      ? ELEC_CAP_NONRES_2019 * ELEC_EMISSION_FACTOR_MT_PER_KWH
      : ELEC_CAP_NONRES_2019;
    return [
      'Source: Beverly Hills CAP-derived allocation',
      `Complete nonresidential: ${fmt(total)} ${unit}`,
      'Commercial/industrial split: unavailable',
      mode === 'emissions'
        ? `Experimental factor: ${ELEC_EMISSION_FACTOR_MT_PER_KWH} MT CO2e per kWh (applied to combined total only)`
        : '',
    ].filter(Boolean);
  }

  if (yearHasVerifiedElecNonresSectorSplit(row)) {
    const comKwh = row.commercial.value!;
    const indKwh = row.industrial.value!;
    const seg = buildElecNonresidentialChartSegments(row, mode);
    return [
      'Source: SCE utility release',
      `Commercial: ${fmt(seg.commercial)} ${unit}`,
      `Industrial: ${fmt(seg.industrial)} ${unit}`,
      `Commercial share: ${pctShare(comKwh, comKwh + indKwh)}%`,
      `Industrial share: ${pctShare(indKwh, comKwh + indKwh)}%`,
      'Classification basis: SCE release',
      mode === 'emissions'
        ? `Experimental factor: ${ELEC_EMISSION_FACTOR_MT_PER_KWH} MT CO2e per kWh (common factor on components)`
        : '',
    ].filter(Boolean);
  }

  const seg = buildElecNonresidentialChartSegments(row, mode);
  return [
    `Complete nonresidential: ${fmt(seg.canonicalTotal)} ${unit}`,
    'Commercial/industrial split: Not available for this year',
    `Coverage: ${row.nonresidential.coverage}`,
  ];
}
